import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AgeGroup } from '../types/child';
import { nextReviewState, type ReviewState } from '../utils/spaced-repetition';
import { pushQuizScore, recordCompetencyAttempt, type AdaptiveState, type DifficultyAdjustment } from '../utils/adaptive-difficulty';

// Stable empty array to avoid infinite re-renders from getSnapshot
const EMPTY_STRING_ARRAY: string[] = [];

export interface QuizAttempt {
  quizType: string;
  score: number;
  total: number;
  date: string;
}

export interface GeographyProgress {
  childId: string;
  visitedStates: string[];       // state abbreviations
  masteredStates: string[];      // quiz mastery (80%+ correct 3 times)
  stateQuizAttempts: Record<string, number>; // state abbr -> consecutive correct count
  quizScores: QuizAttempt[];
  visitedContinents: string[];   // continent ids
  masteredContinents: string[];
  compassLevel: number;          // 0-3 progression
  treasureMapsCompleted: number;
  // Country tracking
  visitedCountries: string[];     // country codes (ISO alpha-2)
  masteredCountries: string[];    // 3 consecutive correct in quiz
  countryQuizAttempts: Record<string, number>; // code -> consecutive correct count

  // ── Topic 2: Collection & Passport ──
  /** ISO date the country was first visited. Populated lazily on first visit. */
  firstVisitedCountry?: Record<string, string>;
  /** ISO date the state was first visited. */
  firstVisitedState?: Record<string, string>;
  /** Holographic-tier collectibles. Younger kids: any 100% quiz featuring the
   *  country grants shiny. Upper-elementary: country must be perfected across
   *  2+ quiz types before going shiny. */
  shinyCountries?: string[];
  shinyStates?: string[];
  /** Tracks which quiz types each country/state has been perfected in.
   *  Used to gate strict-shiny for upper-elementary. */
  perfectCountryQuizTypes?: Record<string, string[]>;
  perfectStateQuizTypes?: Record<string, string[]>;

  // ── Topic 4: Spaced repetition + adaptive difficulty ──
  /** Per-code Leitner box state. Drives quiz-question selection and "due" pills. */
  countryReviews?: Record<string, ReviewState>;
  stateReviews?: Record<string, ReviewState>;
  continentReviews?: Record<string, ReviewState>;
  featureReviews?: Record<string, ReviewState>;
  /** Reviews for non-geographic assets (folk-tale, phrase, instrument, recipe,
   *  craft, famous-person, expedition, endangered-species). Keys are compound:
   *  `${kind}:${code}` — see `assetReviewKey()`. */
  assetReviews?: Record<string, ReviewState>;
  /** Per-child rolling-accuracy + difficulty-adjustment. */
  adaptive?: AdaptiveState;

  // ── Topic 7: Country deep-dive course progress ──
  /** Per-country, set of day numbers (1-10) the kid has completed. */
  courseProgress?: Record<string, number[]>;
}

/** Categories of code we track reviews for. Each maps to a different store field. */
export type ReviewKind = 'country' | 'state' | 'continent' | 'feature' | 'asset';

/** Build the compound key used by `kind === 'asset'` review entries. */
export function assetReviewKey(assetKind: string, code: string): string {
  return `${assetKind}:${code.toUpperCase()}`;
}

interface GeographyState {
  progress: GeographyProgress[];

  // State actions
  markStateVisited: (childId: string, stateAbbr: string) => void;
  toggleStateVisited: (childId: string, stateAbbr: string) => void;
  recordQuizCorrect: (childId: string, stateAbbr: string) => void;
  recordQuizIncorrect: (childId: string, stateAbbr: string) => void;
  addQuizAttempt: (childId: string, attempt: Omit<QuizAttempt, 'date'>) => void;
  markContinentVisited: (childId: string, continentId: string) => void;
  advanceCompassLevel: (childId: string) => void;
  completeTreasureMap: (childId: string) => void;

  // Country actions
  markCountryVisited: (childId: string, countryCode: string) => void;
  unmarkCountryVisited: (childId: string, countryCode: string) => void;
  recordCountryQuizCorrect: (childId: string, countryCode: string) => void;
  recordCountryQuizIncorrect: (childId: string, countryCode: string) => void;

  // Topic 2: Collection & Passport
  /** Called by quizzes on 100% pass with the codes that appeared. Younger kids
   *  get shiny immediately; upper-elementary must perfect 2+ distinct quiz types.
   *  Returns the codes that were *newly* promoted to shiny on this call so the
   *  caller can fire the SHINY celebration only when something actually changed. */
  markPerfectCountries: (childId: string, codes: string[], quizType: string, ageGroup: AgeGroup) => string[];
  markPerfectStates: (childId: string, abbrs: string[], quizType: string, ageGroup: AgeGroup) => string[];
  /** One-time-style retroactive promotion for kids who already mastered codes
   *  before Topic 2 shipped: if the kid has any 100% quiz score in the right
   *  family (world-... or us-..., or state-knowledge) and the code is mastered,
   *  promote it to shiny. Idempotent — safe to call on every collection-view
   *  mount. */
  backfillShiniesFromHistory: (childId: string) => void;

  // ── Topic 4: Spaced repetition + adaptive difficulty ──
  /** Record a single answer in the Leitner schedule. Called after each question. */
  recordReview: (childId: string, kind: ReviewKind, code: string, correct: boolean) => void;
  /** Record a quiz-completion percent into the adaptive-difficulty engine.
   *  Called once at quiz completion. May silently change `difficultyAdjustment`. */
  recordQuizForAdaptive: (childId: string, percent: number) => void;
  /** Record a single per-question attempt against one or more dimensions
   *  (region, qtype, etc.). Drives multi-dimensional weakness targeting. */
  recordCompetency: (
    childId: string,
    dimensions: Array<{ dimension: string; value: string }>,
    correct: boolean,
  ) => void;
  /** Read the current per-child difficulty adjustment (-1 / 0 / +1). */
  getDifficultyAdjustment: (childId: string) => DifficultyAdjustment;

  // ── Topic 7: Country course progress ──
  /** Mark a single course day complete. Idempotent — re-completing is a no-op. */
  markCourseDayComplete: (childId: string, countryCode: string, day: number) => void;
  /** Reset course progress for a country (lets kids start over). */
  resetCourseProgress: (childId: string, countryCode: string) => void;

  // Getters
  getProgress: (childId: string) => GeographyProgress;
  getVisitedStates: (childId: string) => string[];
  getMasteredStates: (childId: string) => string[];
  getBestQuizScore: (childId: string, quizType: string) => number;
}

function emptyProgress(childId: string): GeographyProgress {
  return {
    childId,
    visitedStates: [],
    masteredStates: [],
    stateQuizAttempts: {},
    quizScores: [],
    visitedContinents: [],
    masteredContinents: [],
    compassLevel: 0,
    treasureMapsCompleted: 0,
    visitedCountries: [],
    masteredCountries: [],
    countryQuizAttempts: {},
  };
}

export const useGeographyStore = create<GeographyState>()(
  persist(
    (set, get) => ({
      progress: [],

      markStateVisited: (childId, stateAbbr) => {
        // Full ISO timestamp (not just YYYY-MM-DD) so passport sort breaks ties
        // precisely when two visits land on the same calendar day.
        const today = new Date().toISOString();
        set(state => {
          const prog = state.progress.find(p => p.childId === childId);
          if (prog) {
            if (prog.visitedStates.includes(stateAbbr)) return state;
            const firstVisitedState = { ...(prog.firstVisitedState ?? {}), [stateAbbr]: today };
            return {
              progress: state.progress.map(p =>
                p.childId === childId
                  ? { ...p, visitedStates: [...p.visitedStates, stateAbbr], firstVisitedState }
                  : p
              ),
            };
          }
          return {
            progress: [...state.progress, {
              ...emptyProgress(childId),
              visitedStates: [stateAbbr],
              firstVisitedState: { [stateAbbr]: today },
            }],
          };
        });
      },

      toggleStateVisited: (childId, stateAbbr) => {
        set(state => {
          const prog = state.progress.find(p => p.childId === childId);
          if (prog) {
            const visited = prog.visitedStates.includes(stateAbbr)
              ? prog.visitedStates.filter(s => s !== stateAbbr)
              : [...prog.visitedStates, stateAbbr];
            return {
              progress: state.progress.map(p =>
                p.childId === childId ? { ...p, visitedStates: visited } : p
              ),
            };
          }
          return {
            progress: [...state.progress, { ...emptyProgress(childId), visitedStates: [stateAbbr] }],
          };
        });
      },

      recordQuizCorrect: (childId, stateAbbr) => {
        set(state => {
          const prog = state.progress.find(p => p.childId === childId) ?? emptyProgress(childId);
          const attempts = { ...prog.stateQuizAttempts };
          attempts[stateAbbr] = (attempts[stateAbbr] ?? 0) + 1;
          const mastered = attempts[stateAbbr] >= 3 && !prog.masteredStates.includes(stateAbbr)
            ? [...prog.masteredStates, stateAbbr]
            : prog.masteredStates;

          const existing = state.progress.find(p => p.childId === childId);
          if (existing) {
            return {
              progress: state.progress.map(p =>
                p.childId === childId
                  ? { ...p, stateQuizAttempts: attempts, masteredStates: mastered }
                  : p
              ),
            };
          }
          return {
            progress: [...state.progress, { ...prog, stateQuizAttempts: attempts, masteredStates: mastered }],
          };
        });
      },

      recordQuizIncorrect: (childId, stateAbbr) => {
        set(state => {
          const existing = state.progress.find(p => p.childId === childId);
          if (existing) {
            const attempts = { ...existing.stateQuizAttempts };
            attempts[stateAbbr] = 0;
            return {
              progress: state.progress.map(p =>
                p.childId === childId ? { ...p, stateQuizAttempts: attempts } : p
              ),
            };
          }
          return state;
        });
      },

      addQuizAttempt: (childId, attempt) => {
        set(state => {
          const entry: QuizAttempt = { ...attempt, date: new Date().toISOString() };
          const existing = state.progress.find(p => p.childId === childId);
          if (existing) {
            return {
              progress: state.progress.map(p =>
                p.childId === childId
                  ? { ...p, quizScores: [...p.quizScores, entry] }
                  : p
              ),
            };
          }
          return {
            progress: [...state.progress, { ...emptyProgress(childId), quizScores: [entry] }],
          };
        });
      },

      markContinentVisited: (childId, continentId) => {
        set(state => {
          const existing = state.progress.find(p => p.childId === childId);
          if (existing) {
            if (existing.visitedContinents.includes(continentId)) return state;
            return {
              progress: state.progress.map(p =>
                p.childId === childId
                  ? { ...p, visitedContinents: [...p.visitedContinents, continentId] }
                  : p
              ),
            };
          }
          return {
            progress: [...state.progress, { ...emptyProgress(childId), visitedContinents: [continentId] }],
          };
        });
      },

      advanceCompassLevel: (childId) => {
        set(state => {
          const existing = state.progress.find(p => p.childId === childId);
          if (existing) {
            return {
              progress: state.progress.map(p =>
                p.childId === childId
                  ? { ...p, compassLevel: Math.min(p.compassLevel + 1, 3) }
                  : p
              ),
            };
          }
          return {
            progress: [...state.progress, { ...emptyProgress(childId), compassLevel: 1 }],
          };
        });
      },

      completeTreasureMap: (childId) => {
        set(state => {
          const existing = state.progress.find(p => p.childId === childId);
          if (existing) {
            return {
              progress: state.progress.map(p =>
                p.childId === childId
                  ? { ...p, treasureMapsCompleted: p.treasureMapsCompleted + 1 }
                  : p
              ),
            };
          }
          return {
            progress: [...state.progress, { ...emptyProgress(childId), treasureMapsCompleted: 1 }],
          };
        });
      },

      // ── Country actions ──

      markCountryVisited: (childId, countryCode) => {
        // Full ISO timestamp — see markStateVisited for rationale.
        const today = new Date().toISOString();
        set(state => {
          const prog = state.progress.find(p => p.childId === childId);
          if (prog) {
            if (prog.visitedCountries?.includes(countryCode)) return state;
            const firstVisitedCountry = { ...(prog.firstVisitedCountry ?? {}), [countryCode]: today };
            return {
              progress: state.progress.map(p =>
                p.childId === childId
                  ? { ...p, visitedCountries: [...(p.visitedCountries || []), countryCode], firstVisitedCountry }
                  : p
              ),
            };
          }
          return {
            progress: [...state.progress, {
              ...emptyProgress(childId),
              visitedCountries: [countryCode],
              firstVisitedCountry: { [countryCode]: today },
            }],
          };
        });
      },

      unmarkCountryVisited: (childId, countryCode) => {
        set(state => {
          const prog = state.progress.find(p => p.childId === childId);
          if (prog) {
            return {
              progress: state.progress.map(p =>
                p.childId === childId
                  ? { ...p, visitedCountries: (p.visitedCountries || []).filter(c => c !== countryCode) }
                  : p
              ),
            };
          }
          return state;
        });
      },

      recordCountryQuizCorrect: (childId, countryCode) => {
        set(state => {
          const prog = state.progress.find(p => p.childId === childId) ?? emptyProgress(childId);
          const attempts = { ...(prog.countryQuizAttempts || {}) };
          attempts[countryCode] = (attempts[countryCode] ?? 0) + 1;
          const mastered = attempts[countryCode] >= 3 && !(prog.masteredCountries || []).includes(countryCode)
            ? [...(prog.masteredCountries || []), countryCode]
            : (prog.masteredCountries || []);

          const existing = state.progress.find(p => p.childId === childId);
          if (existing) {
            return {
              progress: state.progress.map(p =>
                p.childId === childId
                  ? { ...p, countryQuizAttempts: attempts, masteredCountries: mastered }
                  : p
              ),
            };
          }
          return {
            progress: [...state.progress, { ...prog, countryQuizAttempts: attempts, masteredCountries: mastered }],
          };
        });
      },

      recordCountryQuizIncorrect: (childId, countryCode) => {
        set(state => {
          const existing = state.progress.find(p => p.childId === childId);
          if (existing) {
            const attempts = { ...(existing.countryQuizAttempts || {}) };
            attempts[countryCode] = 0;
            return {
              progress: state.progress.map(p =>
                p.childId === childId ? { ...p, countryQuizAttempts: attempts } : p
              ),
            };
          }
          return state;
        });
      },

      // ── Topic 2: shiny collectibles ──
      // Younger kids: any 100% quiz featuring the code grants shiny on the spot.
      // Upper-elementary: code only goes shiny once it's been perfected across
      // 2+ distinct quiz types (we track perfect quiz types per code).

      markPerfectCountries: (childId, codes, quizType, ageGroup) => {
        if (codes.length === 0) return [];
        const strict = ageGroup === 'upper-elementary';
        const newlyShiny: string[] = [];
        set(state => {
          const prog = state.progress.find(p => p.childId === childId) ?? emptyProgress(childId);
          const perfectMap = { ...(prog.perfectCountryQuizTypes ?? {}) };
          const previousShiny = new Set(prog.shinyCountries ?? []);
          const shinySet = new Set(previousShiny);
          for (const code of codes) {
            const types = new Set(perfectMap[code] ?? []);
            types.add(quizType);
            perfectMap[code] = [...types];
            const eligible = strict ? types.size >= 2 : true;
            if (eligible && !previousShiny.has(code)) {
              shinySet.add(code);
              newlyShiny.push(code);
            } else if (eligible) {
              shinySet.add(code);
            }
          }
          const next = {
            ...prog,
            perfectCountryQuizTypes: perfectMap,
            shinyCountries: [...shinySet],
          };
          const has = state.progress.find(p => p.childId === childId);
          return {
            progress: has
              ? state.progress.map(p => p.childId === childId ? next : p)
              : [...state.progress, next],
          };
        });
        return newlyShiny;
      },

      markPerfectStates: (childId, abbrs, quizType, ageGroup) => {
        if (abbrs.length === 0) return [];
        const strict = ageGroup === 'upper-elementary';
        const newlyShiny: string[] = [];
        set(state => {
          const prog = state.progress.find(p => p.childId === childId) ?? emptyProgress(childId);
          const perfectMap = { ...(prog.perfectStateQuizTypes ?? {}) };
          const previousShiny = new Set(prog.shinyStates ?? []);
          const shinySet = new Set(previousShiny);
          for (const abbr of abbrs) {
            const types = new Set(perfectMap[abbr] ?? []);
            types.add(quizType);
            perfectMap[abbr] = [...types];
            const eligible = strict ? types.size >= 2 : true;
            if (eligible && !previousShiny.has(abbr)) {
              shinySet.add(abbr);
              newlyShiny.push(abbr);
            } else if (eligible) {
              shinySet.add(abbr);
            }
          }
          const next = {
            ...prog,
            perfectStateQuizTypes: perfectMap,
            shinyStates: [...shinySet],
          };
          const has = state.progress.find(p => p.childId === childId);
          return {
            progress: has
              ? state.progress.map(p => p.childId === childId ? next : p)
              : [...state.progress, next],
          };
        });
        return newlyShiny;
      },

      backfillShiniesFromHistory: (childId) => {
        set(state => {
          const prog = state.progress.find(p => p.childId === childId);
          if (!prog) return state;

          const hasPerfectWorld = prog.quizScores.some(
            q => q.score === q.total && q.quizType.startsWith('world-'),
          );
          const hasPerfectUS = prog.quizScores.some(
            q => q.score === q.total
              && (q.quizType.startsWith('us-') || q.quizType === 'state-knowledge'),
          );

          // Nothing to backfill if there's no perfect history at all.
          if (!hasPerfectWorld && !hasPerfectUS) return state;

          const shinyCountries = new Set(prog.shinyCountries ?? []);
          const shinyStates = new Set(prog.shinyStates ?? []);
          let mutated = false;

          if (hasPerfectWorld) {
            for (const code of prog.masteredCountries ?? []) {
              if (!shinyCountries.has(code)) {
                shinyCountries.add(code);
                mutated = true;
              }
            }
          }
          if (hasPerfectUS) {
            for (const abbr of prog.masteredStates ?? []) {
              if (!shinyStates.has(abbr)) {
                shinyStates.add(abbr);
                mutated = true;
              }
            }
          }

          if (!mutated) return state;
          return {
            progress: state.progress.map(p =>
              p.childId === childId
                ? { ...p, shinyCountries: [...shinyCountries], shinyStates: [...shinyStates] }
                : p,
            ),
          };
        });
      },

      // ── Topic 4: spaced repetition + adaptive difficulty ──

      recordReview: (childId, kind, code, correct) => {
        set(state => {
          const prog = state.progress.find(p => p.childId === childId) ?? emptyProgress(childId);
          const fieldKey = (
            kind === 'country' ? 'countryReviews'
            : kind === 'state' ? 'stateReviews'
            : kind === 'continent' ? 'continentReviews'
            : kind === 'asset' ? 'assetReviews'
            : 'featureReviews'
          ) as 'countryReviews' | 'stateReviews' | 'continentReviews' | 'featureReviews' | 'assetReviews';
          const currentMap: Record<string, ReviewState> = { ...(prog[fieldKey] ?? {}) };
          currentMap[code] = nextReviewState(currentMap[code], correct);
          const next: GeographyProgress = { ...prog, [fieldKey]: currentMap };
          const exists = state.progress.find(p => p.childId === childId);
          return {
            progress: exists
              ? state.progress.map(p => p.childId === childId ? next : p)
              : [...state.progress, next],
          };
        });
      },

      recordQuizForAdaptive: (childId, percent) => {
        set(state => {
          const prog = state.progress.find(p => p.childId === childId) ?? emptyProgress(childId);
          const adaptive = pushQuizScore(prog.adaptive, percent);
          const next: GeographyProgress = { ...prog, adaptive };
          const exists = state.progress.find(p => p.childId === childId);
          return {
            progress: exists
              ? state.progress.map(p => p.childId === childId ? next : p)
              : [...state.progress, next],
          };
        });
      },

      recordCompetency: (childId, dimensions, correct) => {
        set(state => {
          const prog = state.progress.find(p => p.childId === childId) ?? emptyProgress(childId);
          const adaptive = recordCompetencyAttempt(prog.adaptive, dimensions, correct);
          const next: GeographyProgress = { ...prog, adaptive };
          const exists = state.progress.find(p => p.childId === childId);
          return {
            progress: exists
              ? state.progress.map(p => p.childId === childId ? next : p)
              : [...state.progress, next],
          };
        });
      },

      getDifficultyAdjustment: (childId) => {
        const prog = get().progress.find(p => p.childId === childId);
        return prog?.adaptive?.difficultyAdjustment ?? 0;
      },

      // ── Topic 7: course progress ──

      markCourseDayComplete: (childId, countryCode, day) => {
        set(state => {
          const prog = state.progress.find(p => p.childId === childId) ?? emptyProgress(childId);
          const courses = { ...(prog.courseProgress ?? {}) };
          const existing = new Set(courses[countryCode] ?? []);
          existing.add(day);
          courses[countryCode] = [...existing].sort((a, b) => a - b);
          const next: GeographyProgress = { ...prog, courseProgress: courses };
          const has = state.progress.find(p => p.childId === childId);
          return {
            progress: has
              ? state.progress.map(p => p.childId === childId ? next : p)
              : [...state.progress, next],
          };
        });
      },

      resetCourseProgress: (childId, countryCode) => {
        set(state => {
          const prog = state.progress.find(p => p.childId === childId);
          if (!prog || !prog.courseProgress?.[countryCode]) return state;
          const courses = { ...prog.courseProgress };
          delete courses[countryCode];
          return {
            progress: state.progress.map(p =>
              p.childId === childId ? { ...p, courseProgress: courses } : p,
            ),
          };
        });
      },

      getProgress: (childId) => {
        return get().progress.find(p => p.childId === childId) ?? emptyProgress(childId);
      },

      getVisitedStates: (childId) => {
        return get().progress.find(p => p.childId === childId)?.visitedStates ?? EMPTY_STRING_ARRAY;
      },

      getMasteredStates: (childId) => {
        return get().progress.find(p => p.childId === childId)?.masteredStates ?? EMPTY_STRING_ARRAY;
      },

      getBestQuizScore: (childId, quizType) => {
        const prog = get().progress.find(p => p.childId === childId);
        if (!prog) return 0;
        const attempts = prog.quizScores.filter(q => q.quizType === quizType);
        if (attempts.length === 0) return 0;
        return Math.max(...attempts.map(a => Math.round((a.score / a.total) * 100)));
      },
    }),
    {
      name: 'homeschool-geography',
      version: 1,
      migrate: (persistedState, version) => {
        const state = persistedState as GeographyState;
        if (version < 1 && state?.progress) {
          // v0 → v1: drop quiz attempts from quiz types affected by a scoring bug
          // where score state was never accumulated, so stored attempts only
          // reflected the last question's correctness (0 or 1 of N).
          const buggyQuizTypes = new Set([
            'world-knowledge',
            'map-skills',
            'physical-geography',
            'physical-geography-map',
            'continents-map',
          ]);
          state.progress = state.progress.map(p => ({
            ...p,
            quizScores: (p.quizScores || []).filter(q => !buggyQuizTypes.has(q.quizType)),
          }));
        }
        return state;
      },
    }
  )
);
